/**
 * Presenting an estimate or a change order: the rep's side of the customer's
 * document (ADR 0325).
 *
 * Only the chrome around the document is here — the buttons, the switches,
 * the warnings and the sentences the app tells the rep. Every word the
 * customer reads on the proposal, the change order or the PDF stays in
 * English, and a heading quoted inside one of these sentences is quoted in
 * English too, because that is what the rep will see on the page below.
 */
export const present: Readonly<Record<string, string>> = {
  // Shared buttons and labels
  Cancel: "Cancelar",
  Preview: "Vista previa",
  Present: "Presentar",
  "Preparing…": "Preparando…",
  "One moment…": "Un momento…",
  Link: "Enlace",
  "Copy link": "Copiar enlace",
  "Email it": "Enviar por email",
  "Email the link": "Enviar el enlace por email",
  "Sign on this device": "Firmar en este dispositivo",
  "Back to edit": "Volver a editar",
  Name: "Nombre",
  Email: "Email",
  Phone: "Teléfono",
  "the customer": "el cliente",
  "this estimate": "este presupuesto",

  // Present Estimate — the build screen
  "Present Estimate": "Presentar presupuesto",
  "Job #{n} · {name}": "Trabajo nº {n} · {name}",
  "One blank will print on the contract":
    "Un espacio en blanco se imprimirá en el contrato",
  "{n} blanks will print on the contract":
    "{n} espacios en blanco se imprimirán en el contrato",
  "Fill {fields} in on the job.": "Llena {fields} en el trabajo.",
  "Fill {fields} in under Tools → Emails → Company details.":
    "Llena {fields} en Herramientas → Emails → Datos de la empresa.",
  "You can present without them.": "Puedes presentar sin ellos.",
  "The estimate you are presenting": "El presupuesto que estás presentando",
  "Present this one too": "Presentar este también",
  'Move "{name}" up': 'Subir "{name}"',
  'Move "{name}" down': 'Bajar "{name}"',
  "The customer reads them top to bottom: most expensive first, until you move one.":
    "El cliente los lee de arriba abajo: el más caro primero, hasta que muevas uno.",
  "More than one roof can be bought.": "Se puede comprar más de un techo.",
  'The customer can buy more than one of these roofs together. Untick "Customer can buy more than one of these" on the estimate card if they are alternatives for the same roof.':
    'El cliente puede comprar más de uno de estos techos juntos. Desmarca "Customer can buy more than one of these" en la tarjeta del presupuesto si son alternativas para el mismo techo.',
  "Pages to include": "Páginas a incluir",
  Financing: "Financiamiento",
  "The financing page goes out with this":
    "La página de financiamiento va con este",
  "No financing page on this proposal":
    "Sin página de financiamiento en esta propuesta",
  "Starts as the estimate's own Show financing setting. Off means the page is not in the customer's copy at all; the monthly figure beside each price is a separate thing and stays.":
    "Empieza como el ajuste Mostrar financiamiento del propio presupuesto. Apagado significa que la página no va en la copia del cliente; la cifra mensual junto a cada precio es otra cosa y se queda.",
  "Comparison chart": "Tabla comparativa",
  "The roofs are compared side by side": "Los techos se comparan lado a lado",
  "No comparison chart on this proposal":
    "Sin tabla comparativa en esta propuesta",
  "Color options": "Opciones de color",
  "Working out which color options this carries…":
    "Calculando qué opciones de color lleva…",
  "Nothing on this proposal comes in a choice of colors, so there is no color options page.":
    "Nada en esta propuesta viene en varios colores, así que no hay página de opciones de color.",
  "{product} colors": "Colores de {product}",
  "Put the {product} colors back on the customer's copy":
    "Volver a poner los colores de {product} en la copia del cliente",
  "Leave the {product} colors off the customer's copy":
    "Dejar los colores de {product} fuera de la copia del cliente",
  "Off when nobody is choosing that color — gutters matched to the trim, a repair matching what is on the roof. Remembered on this estimate. With every one off, the page is left out.":
    "Apagado cuando nadie está eligiendo ese color — canalones a juego con la moldura, una reparación que iguala lo que hay en el techo. Se recuerda en este presupuesto. Con todos apagados, la página se omite.",
  "Upgrades to offer": "Mejoras a ofrecer",
  "No measurement to price it from": "No hay medida para ponerle precio",
  "Quantity of {name}": "Cantidad de {name}",
  "Not a number — priced at {qty}": "No es un número — cobrado a {qty}",
  "Included in {names}": "Incluido en {names}",
  "The customer ticks these under the packages. Anything already in a package shows as Included there and adds nothing to its price. Type over a quantity to change it on this proposal only.":
    "El cliente marca estas bajo los paquetes. Lo que ya está en un paquete aparece ahí como Incluido y no suma nada a su precio. Escribe sobre una cantidad para cambiarla solo en esta propuesta.",
  "Who it is for": "Para quién es",
  "Still checking which mailboxes you can send from.":
    "Todavía verificando desde qué buzones puedes enviar.",
  "Roof Board could not read the connected mailboxes: {error}":
    "Roof Board no pudo leer los buzones conectados: {error}",
  "No Google account is connected to send from. Connect yours under Tools → Integrations, then reopen this screen.":
    "No hay ninguna cuenta de Google conectada para enviar. Conecta la tuya en Herramientas → Integraciones y vuelve a abrir esta pantalla.",
  "{warning} Tick the box above to send it anyway.":
    "{warning} Marca la casilla de arriba para enviarlo de todos modos.",
  "Put these 1 item under every package on this proposal?":
    "¿Poner este elemento bajo cada paquete de esta propuesta?",
  "Put these {n} items under every package on this proposal?":
    "¿Poner estos {n} elementos bajo cada paquete de esta propuesta?",
  "OK adds them to all {count}. Cancel keeps them on this estimate only.":
    "OK los agrega a los {count}. Cancelar los deja solo en este presupuesto.",

  // Present Estimate — the preview
  "Job #{n} · nothing has been sent": "Trabajo nº {n} · no se ha enviado nada",
  "This document shows the customer a square count. Read the box above and tick it to send it anyway.":
    "Este documento muestra al cliente un conteo de cuadros. Lee el recuadro de arriba y márcalo para enviarlo de todos modos.",
  "Squares are showing.": "Se ven los cuadros.",
  "Send it anyway — I have read it and I am happy for the customer to see this.":
    "Enviarlo de todos modos — lo leí y estoy de acuerdo con que el cliente lo vea.",
  "This is what the customer will see.": "Esto es lo que verá el cliente.",
  "Nothing has been sent and no link exists yet. The customer's copy is created — and locked — the moment you copy the link, email it, or hand them this device.":
    "No se ha enviado nada y todavía no existe ningún enlace. La copia del cliente se crea — y se bloquea — en el momento en que copias el enlace, lo envías por email o le entregas este dispositivo.",
  "Email it is off.": "Enviar por email está apagado.",
  "{why} Copy link and Sign on this device still work.":
    "{why} Copiar enlace y Firmar en este dispositivo siguen funcionando.",
  "Pages on this proposal": "Páginas en esta propuesta",
  "How Your Roof Goes On": "Cómo se instala su techo",
  "Walkthrough videos": "Videos explicativos",
  "Gutter guard slides": "Diapositivas del protector de canalón",
  "See Our Work": "Nuestros trabajos",
  'Put "{name}" back on the customer\'s copy':
    'Volver a poner "{name}" en la copia del cliente',
  'Leave "{name}" off the customer\'s copy':
    'Dejar "{name}" fuera de la copia del cliente',
  "Off leaves the page out of this proposal only. The next one starts with every page on.":
    "Apagado omite la página solo en esta propuesta. La siguiente empieza con todas las páginas encendidas.",
  "Edit the words of": "Editar las palabras de",
  "Edit the words of {name}": "Editar las palabras de {name}",
  "Package name": "Nombre del paquete",
  "Key Differentiator": "Diferenciador clave",
  "The one line the customer reads under the package name, telling them why this package rather than the other.":
    "La línea que el cliente lee bajo el nombre del paquete, diciéndole por qué este paquete y no el otro.",
  'Blank reads the shingle\'s own: "{line}".':
    'En blanco se lee la de la propia teja: "{line}".',
  "Blank leaves it off.": "En blanco se omite.",
  "One per line — e.g.": "Una por línea — p. ej.",
  "One per line. Leaving the box asks whether they belong under every package or only this one.":
    "Una por línea. Al salir del recuadro se pregunta si van bajo cada paquete o solo en este.",
  "Show {name}": "Mostrar {name}",
  "Name of {name}": "Nombre de {name}",
  "No scope of work — the customer reads the name only":
    "Sin alcance de trabajo — el cliente lee solo el nombre",
  "Each package carries its own words, its own order and its own key differentiator. Every box saves onto the estimate when you leave it; a priced line's scope is the same one the builder shows.":
    "Cada paquete lleva sus propias palabras, su propio orden y su propio diferenciador clave. Cada recuadro se guarda en el presupuesto al salir de él; el alcance de una línea con precio es el mismo que muestra el constructor.",

  // Present Estimate — the link
  "Ready to sign": "Listo para firmar",
  "The document is locked and the link is live for 30 days. Editing this job from now on will not change what the customer sees — to change their copy, present it again, which replaces this one.":
    "El documento está bloqueado y el enlace estará activo 30 días. Editar este trabajo de ahora en adelante no cambiará lo que ve el cliente — para cambiar su copia, preséntalo de nuevo, lo que reemplaza este.",
  "This is revision {n}. Any link sent to this customer before now has stopped working, so there is only ever one document they can sign.":
    "Esta es la revisión {n}. Cualquier enlace enviado a este cliente antes de ahora dejó de funcionar, así que solo hay un documento que pueda firmar.",
  "Email it below, or paste the link wherever you already talk to this customer. Texting is still by hand.":
    "Envíalo por email abajo, o pega el enlace donde ya hablas con este cliente. Los mensajes de texto siguen siendo a mano.",
  "{why} Until then, paste the link wherever you already talk to this customer.":
    "{why} Mientras tanto, pega el enlace donde ya hablas con este cliente.",
  "Email the estimate to {name}": "Enviar el presupuesto por email a {name}",
  "They can read and sign it on their phone.":
    "Puede leerlo y firmarlo en su teléfono.",
  "Send the estimate": "Enviar el presupuesto",

  // Present change order
  "Present change order": "Presentar orden de cambio",
  "Job {n}": "Trabajo {n}",
  "Checking your email connection…": "Verificando tu conexión de email…",
  "Connect your Google account in Tools to email from here.":
    "Conecta tu cuenta de Google en Herramientas para enviar email desde aquí.",
  "What the customer will see": "Lo que verá el cliente",
  "Ready to approve": "Listo para aprobar",
  "Change order {n} is locked and the link is live for 30 days. Editing these lines from now on will not change what the customer sees — to change their copy, present it again, which replaces this one.":
    "La orden de cambio {n} está bloqueada y el enlace estará activo 30 días. Editar estas líneas de ahora en adelante no cambiará lo que ve el cliente — para cambiar su copia, preséntala de nuevo, lo que reemplaza esta.",
  "Email change order {n} to {name}":
    "Enviar la orden de cambio {n} por email a {name}",
  "They can read it and approve it on their phone.":
    "Puede leerla y aprobarla en su teléfono.",
  "Every change order on this estimate has been signed. Add one to send another.":
    "Todas las órdenes de cambio de este presupuesto están firmadas. Agrega una para enviar otra.",
  "Going on this document:": "Va en este documento:",
  "New contract total": "Nuevo total del contrato",
  "Paid so far": "Pagado hasta ahora",
  "Balance after this": "Saldo después de esto",
  'No change order terms are written yet, so this will go out with nothing under "Read the terms". Tools → Change Order Terms.':
    'Todavía no hay términos de orden de cambio escritos, así que esto saldrá sin nada bajo "Read the terms". Herramientas → Términos de orden de cambio.',
  "Presenting locks what the customer reads and creates their link. It does not send anything — you choose how it goes out next.":
    "Presentar bloquea lo que lee el cliente y crea su enlace. No envía nada — tú eliges cómo sale después.",

  // The blocks of the proposal
  "What the customer reads": "Lo que lee el cliente",
  "Switched off means left out of the proposal — the line items stay on the estimate either way. The arrows change the order the customer reads them in, on this option only.":
    "Apagado significa fuera de la propuesta — las líneas se quedan en el presupuesto de todos modos. Las flechas cambian el orden en que el cliente las lee, solo en esta opción.",

  // Photos of their roof
  "Photos of their roof": "Fotos de su techo",
  "{name} is not an image.": "{name} no es una imagen.",
  "Take this photo off the proposal?": "¿Quitar esta foto de la propuesta?",
  "Take this photo off the proposal": "Quitar esta foto de la propuesta",
  "Uploading…": "Subiendo…",
  "Upload photos": "Subir fotos",
  "They appear on the customer's copy under “What we found”.":
    "Aparecen en la copia del cliente bajo “What we found”.",
  "1 photo, in this order.": "1 foto, en este orden.",
  "{n} photos, in this order.": "{n} fotos, en este orden.",
  "Caption (optional)": "Leyenda (opcional)",

  // Our paperwork
  "Our paperwork": "Nuestra documentación",
  "Nothing in the library yet. Add the licence, the insurance certificate and anything else you send often under Tools → Company Docs.":
    "Todavía no hay nada en la biblioteca. Agrega la licencia, el certificado de seguro y cualquier otra cosa que envíes seguido en Herramientas → Documentos de la empresa.",
  "None attached. They appear on the customer's copy under “Our paperwork”.":
    "Ninguno adjunto. Aparecen en la copia del cliente bajo “Our paperwork”.",
  "1 attached, in this order, under “Our paperwork”.":
    "1 adjunto, en este orden, bajo “Our paperwork”.",
  "{n} attached, in this order, under “Our paperwork”.":
    "{n} adjuntos, en este orden, bajo “Our paperwork”.",

  // Apply these changes to the other estimates?
  "Apply these changes to the other estimates?":
    "¿Aplicar estos cambios a los otros presupuestos?",
  "You changed the {bucket} on {source}.": "Cambiaste {bucket} en {source}.",
  "line items": "las líneas",
  "material order": "la orden de materiales",
  "work order": "la orden de trabajo",
  "other expenses": "los otros gastos",
  "No, just this one": "No, solo este",
  "Applying…": "Aplicando…",
  "Apply to selected": "Aplicar a los seleccionados",
  "What changed": "Qué cambió",
  "Also apply to": "Aplicar también a",
  "There are no other estimates on this job to apply them to.":
    "No hay otros presupuestos en este trabajo a los que aplicarlos.",
  "— cannot be changed ({why})": "— no se puede cambiar ({why})",
  "no material order": "sin orden de materiales",
  "no work order": "sin orden de trabajo",
  "more than one material order": "más de una orden de materiales",
  "more than one work order": "más de una orden de trabajo",
  "not available": "no disponible",
  signed: "firmado",
  "— already matches": "— ya coincide",
  "— 1 change": "— 1 cambio",
  "— {n} changes": "— {n} cambios",
  "That line": "Esa línea",
  "{name} — {label} has {theirs}, change it to {mine} as well?":
    "{name} — {label} tiene {theirs}, ¿cambiarlo a {mine} también?",

  // Draft with AI, under the line's note
  "🎤 Draft with AI": "🎤 Redactar con IA",
  "Describe the work for the AI draft":
    "Describe el trabajo para el borrador de IA",
  "Say what the job is — “full architectural tear-off with ridge vent” is enough — and AI writes the whole scope of work above for you to edit. It replaces what's in the box.":
    "Di cuál es el trabajo — “full architectural tear-off with ridge vent” es suficiente — y la IA escribe todo el alcance de trabajo arriba para que lo edites. Reemplaza lo que hay en el recuadro.",
  "◼ Stop Listening": "◼ Dejar de escuchar",
  "🎤 Start Talking": "🎤 Empezar a hablar",
  "This browser has no dictation — type it instead, same result.":
    "Este navegador no tiene dictado — escríbelo en su lugar, mismo resultado.",
  "Writing the scope…": "Escribiendo el alcance…",
  "Write the Scope": "Escribir el alcance",
  "Listening… it stops on its own when you go quiet.":
    "Escuchando… se detiene solo cuando te quedas callado.",
  "Could not draft the scope. Try again.":
    "No se pudo redactar el alcance. Inténtalo de nuevo.",
  "The browser is not letting this page use the microphone. Allow it for this site, then try again — or just type the job below.":
    "El navegador no deja que esta página use el micrófono. Permítelo para este sitio y vuelve a intentar — o simplemente escribe el trabajo abajo.",
  "This browser's dictation service is unavailable. Type the job below instead — the draft is exactly the same.":
    "El servicio de dictado de este navegador no está disponible. Escribe el trabajo abajo — el borrador es exactamente el mismo.",
  "No microphone was found. Type the job below instead — the draft is exactly the same.":
    "No se encontró ningún micrófono. Escribe el trabajo abajo — el borrador es exactamente el mismo.",
  "Dictation could not reach the internet. Check the signal, or type the job below.":
    "El dictado no pudo conectarse a internet. Revisa la señal, o escribe el trabajo abajo.",
  "Dictation stopped unexpectedly. Type the job below instead — the draft is exactly the same.":
    "El dictado se detuvo inesperadamente. Escribe el trabajo abajo — el borrador es exactamente el mismo.",
  "Dictation would not start. Type the job below instead — the draft is exactly the same.":
    "El dictado no arrancó. Escribe el trabajo abajo — el borrador es exactamente el mismo.",
};
